import React, { useState, useEffect } from 'react';
import { authenticatedFetch } from '../../utils/api';
import styles from './Leaderboard.module.css';

const Leaderboard = () => {
    const [leaderboardData, setLeaderboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });

    const fetchLeaderboard = async (month) => {
        try {
            setLoading(true);
            const data = await authenticatedFetch(`/api/leaderboard?month=${month}`);
            setLeaderboardData(data);
            setError(null);
        } catch (err) {
            setError('Failed to load leaderboard data');
            console.error('Error fetching leaderboard:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLeaderboard(selectedMonth);
    }, [selectedMonth]);

    const formatMonth = (monthStr) => {
        const [year, month] = monthStr.split('-');
        return new Date(year, month - 1).toLocaleString('default', {
            month: 'long',
            year: 'numeric'
        });
    };

    if (loading) {
        return <div className={styles.loadingContainer}>
            <div className={styles.spinner} />
            <span>Loading leaderboard data...</span>
        </div>;
    }

    if (error) {
        return <div className={styles.errorContainer}>
            <div className={styles.errorContent}>
                <span className={styles.errorIcon}>⚠️</span>
                <span>{error}</span>
            </div>
        </div>;
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h2 className={styles.title}>Performance Rankings</h2>
                <div className={styles.monthSelector}>
                    <select
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        className={styles.select}
                    >
                        {Array.from({ length: 6 }).map((_, i) => {
                            const date = new Date();
                            date.setMonth(date.getMonth() - i);
                            const monthStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                            return (
                                <option key={monthStr} value={monthStr}>
                                    {formatMonth(monthStr)}
                                </option>
                            );
                        })}
                    </select>
                </div>
            </div>

            <div className={styles.card}>
                <div className={styles.tableHeader}>
                    <div className={styles.column}>Rank</div>
                    <div className={styles.column}>Participant</div>
                    <div className={styles.column}>Performance Score</div>
                    <div className={styles.column}>Streak (days)</div>
                </div>

                <div className={styles.tableBody}>
                    {leaderboardData?.users.map((user, index) => (
                        <div
                            key={user.user_id}
                            className={`${styles.row} ${index < 3 ? styles[`rank${index + 1}`] : ''}`}
                        >
                            <div className={styles.column}>
                                <span className={styles.rankNumber}>{index + 1}</span>
                            </div>
                            <div className={styles.column}>{user.username}</div>
                            <div className={styles.column}>
                                <span className={styles.score}>{user.monthly_xp}</span>
                            </div>
                            <div className={styles.column}>
                                <span className={styles.streakInfo}>
                                    Current: {user.current_streak} | Peak: {user.longest_streak}
                                </span>
                            </div>
                        </div>
                    ))}

                    {leaderboardData?.users.length === 0 && (
                        <div className={styles.emptyState}>
                            No performance data available for this period
                        </div>
                    )}
                </div>
            </div>

            <div className={styles.infoCard}>
                <h3 className={styles.infoTitle}>Monthly Performance Incentives</h3>
                <div className={styles.incentiveGrid}>
                    <div className={styles.incentive}>
                        <span className={styles.rank}>1st</span>
                        <span className={styles.reward}>60 standard minutes</span>
                    </div>
                    <div className={styles.incentive}>
                        <span className={styles.rank}>2nd</span>
                        <span className={styles.reward}>30 standard minutes</span>
                    </div>
                    <div className={styles.incentive}>
                        <span className={styles.rank}>3rd</span>
                        <span className={styles.reward} >15 standard minutes</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Leaderboard;